/**
 * Shared regex patterns to avoid duplication across SNS/WYSIWYG modules.
 * Centralized as a class with static properties and helper methods.
 */

interface SNSPlatformConfig {
    regex: RegExp;
    icon: string;
    color: string;
    name: string;
    embedSupport: boolean;
}

interface SNSPlatformMap {
    [key: string]: SNSPlatformConfig;
    youtube: SNSPlatformConfig;
    facebook: SNSPlatformConfig;
    instagram: SNSPlatformConfig;
    x: SNSPlatformConfig;
    tiktok: SNSPlatformConfig;
}

interface SNSDefaultOptions {
    enableEmbeds: boolean;
    enableIcons: boolean;
    enableLinkHiding: boolean;
    debugMode: boolean;
}

export class Patterns {
    public static FACEBOOK_IFRAME = /<iframe[^>]*facebook\.com[^>]*>/gi;
    public static ESCAPED_IFRAME = /&lt;iframe[^]*?&lt;\/iframe&gt;/gi;
    public static IFRAME_SRC = /<iframe[^>]*\ssrc=["']([^"']+)["']/gi;
    public static ESCAPED_IFRAME_STRIP = /&lt;iframe[^&]*?&gt;.*?&lt;\/iframe&gt;/gi;
    public static ESCAPED_SCRIPT = /&lt;script[^&]*?&gt;.*?&lt;\/script&gt;/gi;

    public static FACEBOOK_URL = /https?:\/\/(?:www\.|m\.)?facebook\.com\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/gi;
    public static YOUTUBE_URL = /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/gi;
    public static YOUTUBE_SHORT_URL = /https?:\/\/youtu\.be\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/gi;
    public static INSTAGRAM_URL = /https?:\/\/(?:www\.)?instagram\.com\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/gi;
    public static TWITTER_URL = /https?:\/\/(?:www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/gi;

    public static URL = /https?:\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+(?=\s|<|>|$|[^\u0080-\uFFFF])/gi;
    public static URL_PROTOCOL_PREFIX = /(https?:\/\/)/gi;
    public static PATH_ID =
        /^(https?:\/\/[^/]+\/(?:articles?|news|posts?|stories)\/[A-Za-z0-9_-]{6,64}?)([a-z]{4,}.*)$/i;
    public static YOUTUBE_TRAILING = /^(https?:\/\/[^/]+\/watch\?v=[A-Za-z0-9_-]{11}(?:&[^&=]+=[^&]*)*)([a-z]{4,}.*)$/i;
    public static ANCHOR_HREF = /<a[^>]+href=["']([^"']+)["'][^>]*>/gi;

    public static URL_IN_PARAGRAPH = /<p>(\s*)(https?:\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)(\s*)<\/p>/gi;

    public static IFRAME_HREF_PARAM = /href=([^&"]+)/;
    public static IFRAME_HREF_QUERY_PARAM = /[?&]href=([^&]+)/;
    public static YOUTUBE_EMBED_SRC = /youtube\.com\/embed\/([^?"]+)/;
    public static INSTAGRAM_EMBED_SRC = /instagram\.com\/([^/]+)\/embed/;

    public static OGP_EXCLUDED_SNS_PLATFORMS: RegExp[] = [
        /facebook\.com|fb\.com/i,
        /youtube\.com|youtu\.be/i,
        /instagram\.com/i,
        /twitter\.com|x\.com/i,
        /tiktok\.com/i,
    ];

    public static VALID_BOARD_ID = /^[a-zA-Z0-9_-]+$/;

    public static BOARD_ID_CREATE_PATH = /\/posts\/create\/([^/]+)/;
    public static BOARD_ID_BOARD_PATH = /\/posts\/([^/]+)\/create/;
    public static BOARD_ID_SIMPLE_PATH = /\/([^/]+)\/create/;

    public static PATHNAME_PRODUCTS_ID = /\/products\/([A-Z0-9-]+)/;
    public static PATHNAME_POSTS_ID = /\/posts\/([^/]+)/;
    public static PATHNAME_POSTS_ID_WORD = /\/posts\/(\w+)/;
    public static PATHNAME_REVIEW_PRODUCT_ID = /\/review\/([A-Z]+-\d+)/;

    public static YOUTUBE_LEGACY =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[?&][^\s<>]*)?/g;
    public static YOUTUBE_ID = /(?:v=|\/)([a-zA-Z0-9_-]{11})/;

    public static HTML_ENTITY_AMP = /&amp;/g;
    public static HTML_ENTITY_LT = /&lt;/g;
    public static HTML_ENTITY_GT = /&gt;/g;

    public static CHAR_AMP = /&/g;
    public static CHAR_LT = /</g;
    public static CHAR_GT = />/g;

    public static IFRAME_TAG = /<iframe[^>]*?>.*?<\/iframe>/gi;
    public static IFRAME_SELF_CLOSING = /<iframe[^>]*?\/>/gi;
    public static ESCAPED_IFRAME_STRIP_ALT = /&lt;iframe[^>]*?&gt;[^<]*&lt;\/iframe&gt;/gi;
    public static PARAGRAPH_WITH_ESCAPED_IFRAME = /<p[^>]*?>[\s\S]*?&lt;iframe[\s\S]*?<\/p>/gi;
    public static PARAGRAPH_WITH_IFRAME = /<p[^>]*?>[\s\S]*?<iframe[\s\S]*?<\/p>/gi;
    public static EMPTY_PARAGRAPH = /<p[^>]*?>\s*<\/p>/gi;
    public static EMPTY_PARAGRAPH_SIMPLE = /<p>\s*<\/p>/gi;

    public static reset(pattern: RegExp): RegExp {
        pattern.lastIndex = 0;
        return pattern;
    }

    public static match(content: string, pattern: RegExp): RegExpMatchArray | null {
        return content.match(this.reset(pattern));
    }

    public static test(content: string, pattern: RegExp): boolean {
        this.reset(pattern);
        return pattern.test(content);
    }

    public static escapeRegex(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    public static createUrlInParagraphPattern(url: string): RegExp {
        const escapedUrl = this.escapeRegex(url);
        return new RegExp(`<p>\\s*${escapedUrl}[^<]*</p>`, "gi");
    }

    public static createPlainUrlPattern(url: string): RegExp {
        const escapedUrl = this.escapeRegex(url);
        return new RegExp(escapedUrl, "gi");
    }

    public static createPostEditorImageFilenamePattern(
        boardId: string | null | undefined,
        userId: number | null | undefined,
        baseUrlPath: string | null | undefined,
    ): RegExp {
        const base =
            typeof baseUrlPath === "string" && baseUrlPath.trim() !== ""
                ? baseUrlPath.trim().replace(/\/+$/, "")
                : null;

        if (!base || !boardId) {
            return /$^/;
        }

        const escapedBase = this.escapeRegex(base);
        const escapedBoardId = this.escapeRegex(boardId);
        const uid = typeof userId === "number" && Number.isFinite(userId) && userId > 0 ? String(userId) : "";
        if (!uid) {
            return new RegExp(`${escapedBase}/${escapedBoardId}/(editor_[^"'\\s]+)`, "i");
        }

        const escapedUserId = this.escapeRegex(uid);
        return new RegExp(`${escapedBase}/${escapedBoardId}/${escapedUserId}/(editor_[^"'\\s]+)`, "i");
    }

    public static decodeHtmlEntities(value: string): string {
        return value
            .replace(this.HTML_ENTITY_AMP, "&")
            .replace(this.HTML_ENTITY_LT, "<")
            .replace(this.HTML_ENTITY_GT, ">");
    }

    public static encodeHtmlEntities(value: string): string {
        return value.replace(this.CHAR_AMP, "&amp;").replace(this.CHAR_LT, "&lt;").replace(this.CHAR_GT, "&gt;");
    }

    public static encodeAmpersand(value: string): string {
        return value.replace(this.CHAR_AMP, "&amp;");
    }

    public static decodeAmpersand(value: string): string {
        return value.replace(this.HTML_ENTITY_AMP, "&");
    }

    public static get SNS_PLATFORMS(): SNSPlatformMap {
        return {
            youtube: {
                regex: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[?&][^\s<>]*)?/gi,
                icon: "bi-youtube",
                color: "#ff0000",
                name: "YouTube",
                embedSupport: false,
            },
            facebook: {
                regex: /(?:https?:\/\/)?(?:www\.)?(?:facebook\.com|fb\.com)\/[^\s<>]+/gi,
                icon: "bi-facebook",
                color: "#1877f2",
                name: "Facebook",
                embedSupport: true,
            },
            instagram: {
                regex: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel|tv)\/([a-zA-Z0-9_-]+)(?:\/)?(?:[?&][^\s<>]*)?/gi,
                icon: "bi-instagram",
                color: "#e1306c",
                name: "Instagram",
                embedSupport: false,
            },
            x: {
                regex: /(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/[^/\s<>]+\/status\/([a-zA-Z0-9_]+)(?:[?&][^\s<>]*)?/gi,
                icon: "bi-twitter-x",
                color: "#000000",
                name: "X",
                embedSupport: true,
            },
            tiktok: {
                regex: /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@[^/\s<>]+\/video\/([0-9]+)(?:(?:\?[^\s<>#]*)?(?:#[^\s<>]*)?|[?&][^\s<>]*)?/gi,
                icon: "bi-tiktok",
                color: "#000000",
                name: "TikTok",
                embedSupport: false,
            },
        };
    }

    public static get SNS_DEFAULT_OPTIONS(): SNSDefaultOptions {
        return {
            enableEmbeds: true,
            enableIcons: true,
            enableLinkHiding: true,
            debugMode: false,
        };
    }
}
